import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useGetAccountQuery } from '@/services/accounts';
import { useGetQuotesQuery } from '@/services/quotes';
import { Alert, Badge, Box, Column, Table } from '@palmetto/palmetto-components';
import { useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { BatteryPricingMethodOption, QuoteBatteryDetails, QuoteDocument, QuoteStatus } from 'types';

import { useAuth } from '../auth/authProvider';
import { isAdminUser } from '../auth/RequireAdminUser';
import FormSection from '../Forms/FormSection';
import { HoverOnlyTooltipComponent } from '../Tooltip';

import { EstimatedPricingData } from './hooks/useGetAccountEstimatedPricing';
import './quoteTable.css';

type BadgeType = 'kwhRate' | 'monthlyPayment' | 'epcPpw';

const badgeConfig: Record<BadgeType, { label: string; variant: 'info' | 'success' | 'warning' }> = {
  kwhRate: { label: 'kWh', variant: 'info' },
  monthlyPayment: { label: '$', variant: 'success' },
  epcPpw: { label: 'PPW', variant: 'warning' },
};

type QuoteTableData = {
  productId: string;
  kwhRate: string;
  kwhRateNumeric: number;
  firstMonthlyPayment: string;
  firstMonthlyPaymentNumeric: number;
  ppwNumeric: number;
  solarOnlyEPCPayment: string;
  totalEPCPayment?: string;
  batteryMonthlyPayment?: string;
  totalMonthlyPayment?: string;
};

const findClosestMatches = (
  tableData: QuoteTableData[],
  activeQuote: QuoteDocument | undefined,
): { kwhRateMatch?: string; monthlyPaymentMatch?: string; epcPpwMatch?: string } => {
  if (!activeQuote || tableData.length === 0) return {};

  let kwhRateMatch: string | undefined;
  let monthlyPaymentMatch: string | undefined;
  let epcPpwMatch: string | undefined;

  let minKwhDiff = Infinity;
  let minPaymentDiff = Infinity;
  let minPpwDiff = Infinity;

  tableData.forEach((row) => {
    const kwhDiff = Math.abs(row.kwhRateNumeric - activeQuote.kwhPrice);
    if (kwhDiff < minKwhDiff) {
      minKwhDiff = kwhDiff;
      kwhRateMatch = row.productId;
    }

    const paymentDiff = Math.abs(
      row.firstMonthlyPaymentNumeric - (activeQuote.systemPricingDetails?.[0]?.monthlyPayment ?? 0),
    );
    if (paymentDiff < minPaymentDiff) {
      minPaymentDiff = paymentDiff;
      monthlyPaymentMatch = row.productId;
    }

    const ppwDiff = Math.abs(row.ppwNumeric - activeQuote.pricePerWatt);
    if (ppwDiff < minPpwDiff) {
      minPpwDiff = ppwDiff;
      epcPpwMatch = row.productId;
    }
  });

  return { kwhRateMatch, monthlyPaymentMatch, epcPpwMatch };
};

const dcColumns = [
  {
    heading: 'Solar-only EPC Rate ($/W) without DC',
    dataKey: 'ppwWithoutDC',
    headerClassName: 'wrap',
  },
  {
    heading: 'Solar-only EPC Total Payment without DC',
    headerClassName: 'wrap',
    dataKey: 'solarOnlyEPCPaymentWithoutDC',
  },
];

const batteryColumns = [
  {
    heading: 'Estimated Battery Year 1 Monthly Payment',
    headerClassName: 'wrap',
    dataKey: 'batteryPayment',
  },
  {
    heading: 'Total Year 1 Monthly Payment',
    headerClassName: 'wrap',
    dataKey: 'totalMonthlyPayment',
  },
  {
    heading: 'Total EPC Payment',
    headerClassName: 'wrap',
    dataKey: 'totalEPCPayment',
  },
];

const dcBatteryColumns = [
  {
    heading: 'Total EPC Payment without DC',
    headerClassName: 'wrap',
    dataKey: 'totalEPCPaymentWithoutDC',
  },
];

const effectivePPWColumn = {
  heading: 'Effective EPC Rate ($/W)',
  dataKey: 'effectivePPWRate',
  headerClassName: 'wrap',
};

export const EstimatedPricingTable = ({ pricingData }: { pricingData: EstimatedPricingData }) => {
  const { monthlyPaymentCap = false } = useFlags();
  const { claims } = useAuth();
  const { id } = useParams<{ id: any }>();
  const { data: accountData } = useGetAccountQuery(id, { skip: !id });
  const programType = accountData?.programType;
  const { data: quotes } = useGetQuotesQuery({ id, programType }, { skip: !programType });
  const activeQuote: QuoteDocument | undefined = quotes?.find((q) => q.status === QuoteStatus.active);

  const { values, setFieldValue } = useFormikContext<{
    systemSizeKw?: number;
    systemFirstYearProductionKwh?: number;
    pricingOptionsLimitedByMonthlyPaymentCap?: boolean;
    selectedEscalationRate?: number;
    battery?: QuoteBatteryDetails;
    noPricingOptionsAvailable?: boolean;
  }>();
  const { products, showDCComparison, escalationRates, isDCPricingDifferent, isLoading, isFetching } = pricingData;
  useEffect(() => {
    if (monthlyPaymentCap && !products?.length && !isLoading && !isFetching) {
      setFieldValue('noPricingOptionsAvailable', true);
    } else {
      setFieldValue('noPricingOptionsAvailable', false);
    }

    if (
      monthlyPaymentCap &&
      products?.length &&
      products[0].resultsLimitedByMonthlyPaymentCap &&
      !values.pricingOptionsLimitedByMonthlyPaymentCap
    ) {
      setFieldValue('pricingOptionsLimitedByMonthlyPaymentCap', true);
    }
  }, [
    monthlyPaymentCap,
    products,
    values.pricingOptionsLimitedByMonthlyPaymentCap,
    setFieldValue,
    accountData?.programType,
    isLoading,
    isFetching,
  ]);

  const escalationRateAndProductData = useMemo(() => {
    const escalationRateAndProduct: Array<{ escalationRate: number; tableData: QuoteTableData[] }> = [];
    escalationRates?.forEach((rate) => {
      const productsByEscalationRate = products?.filter((product) => product.escalationRate === rate);
      const kwhRateAndEpcAndFirstMonthlyPayment =
        productsByEscalationRate?.map((product) => ({
          productId: product.productId,
          kwhRate: `$${product.kwhRate}`,
          kwhRateNumeric: product.kwhRate,
          firstMonthlyPayment: currencyFormatter.format(product.monthlyPayments[0].monthlyPayment),
          firstMonthlyPaymentNumeric: product.monthlyPayments[0].monthlyPayment,
          ppwNumeric: product.ppwRate,
          effectivePPWRate: currencyFormatter.format(product.effectivePpwRate),
          ppw: currencyFormatter.format(product.ppwRate),
          ppwWithoutDC: currencyFormatter.format(product.ppwWithoutDC),
          solarOnlyEPCPayment: currencyFormatter.format(product.solarOnlyEPCPayment),
          solarOnlyEPCPaymentWithoutDC: currencyFormatter.format(product.solarOnlyEPCPaymentWithoutDC),
          totalEPCPayment: product.totalEPCPayment ? currencyFormatter.format(product.totalEPCPayment) : undefined,
          totalEPCPaymentWithoutDC: product.totalEPCPaymentWithoutDC
            ? currencyFormatter.format(product.totalEPCPaymentWithoutDC)
            : undefined,
          batteryPayment:
            product.monthlyPayments[0]?.batteryPayment !== undefined
              ? currencyFormatter.format(product.monthlyPayments[0].batteryPayment)
              : undefined,
          totalMonthlyPayment:
            product.monthlyPayments[0]?.totalMonthlyPayment !== undefined
              ? currencyFormatter.format(product.monthlyPayments[0].totalMonthlyPayment)
              : undefined,
        })) || [];
      escalationRateAndProduct.push({
        escalationRate: rate,
        tableData: kwhRateAndEpcAndFirstMonthlyPayment,
      });
    });
    const selectedEscalationRateAndProduct =
      values.selectedEscalationRate !== undefined
        ? escalationRateAndProduct.find((products) => products.escalationRate === values.selectedEscalationRate)
        : escalationRateAndProduct[0];
    return selectedEscalationRateAndProduct;
  }, [products, escalationRates, values.selectedEscalationRate]);

  const closestMatches = useMemo(() => {
    return findClosestMatches(escalationRateAndProductData?.tableData || [], activeQuote);
  }, [escalationRateAndProductData?.tableData, activeQuote]);

  // don't show the DC columns to non admins when the dc pricing is the same
  const shouldShowDcColumns = isDCPricingDifferent || isAdminUser(claims);
  const hasBattery = values?.battery?.pricingMethod === BatteryPricingMethodOption.rawCost;

  const columns = useMemo((): Column[] => {
    const baseColumns: Column[] = [
      {
        heading: 'Solar-only Rate ($/kWh)',
        headerClassName: 'wrap',
        dataKey: 'kwhRate',
        render: (_: any, row: any) => (
          <Box display="flex" direction="row" gap="xs">
            <span>{row.kwhRate}</span>
            {closestMatches.kwhRateMatch === row.productId && (
              <Badge message={badgeConfig.kwhRate.label} variant={badgeConfig.kwhRate.variant} size="sm" />
            )}
          </Box>
        ),
      },
      {
        heading: 'Estimated solar-only year-1 Mo. Payment',
        dataKey: 'firstMonthlyPayment',
        headerClassName: 'wrap',
        render: (_: any, row: any) => (
          <Box display="flex" direction="row" gap="xs">
            <span>{row.firstMonthlyPayment}</span>
            {closestMatches.monthlyPaymentMatch === row.productId && (
              <HoverOnlyTooltipComponent
                content={`Quoted Monthly Payment: ${currencyFormatter.format(activeQuote?.systemPricingDetails?.[0]?.monthlyPayment || 0)}`}
                onClose={() => {}}
              >
                <Badge
                  message={badgeConfig.monthlyPayment.label}
                  variant={badgeConfig.monthlyPayment.variant}
                  size="sm"
                />
              </HoverOnlyTooltipComponent>
            )}
          </Box>
        ),
      },
      {
        heading: 'Solar-only EPC Rate ($/W)',
        dataKey: 'ppw',
        headerClassName: 'wrap',
        render: (_: any, row: any) => (
          <Box display="flex" direction="row" gap="xs">
            <span>{row.ppw}</span>
            {closestMatches.epcPpwMatch === row.productId && (
              <HoverOnlyTooltipComponent
                content={`Quoted EPC Rate: ${currencyFormatter.format(activeQuote?.pricePerWatt || 0)}`}
                onClose={() => {}}
              >
                <Badge message={badgeConfig.epcPpw.label} variant={badgeConfig.epcPpw.variant} size="sm" />
              </HoverOnlyTooltipComponent>
            )}
          </Box>
        ),
      },
      {
        heading: 'Solar-only EPC Total Payment',
        dataKey: 'solarOnlyEPCPayment',
        headerClassName: 'wrap',
      },
    ];

    const result = [...baseColumns];

    if (showDCComparison && shouldShowDcColumns) {
      result.push(...dcColumns);
    }

    if (hasBattery) {
      result.push(...batteryColumns);
      if (showDCComparison && shouldShowDcColumns) {
        result.push(...dcBatteryColumns);
      }
      result.push(effectivePPWColumn);
    }

    return result;
  }, [closestMatches, showDCComparison, shouldShowDcColumns, hasBattery, activeQuote]);

  const showLegend =
    activeQuote && (closestMatches.kwhRateMatch || closestMatches.monthlyPaymentMatch || closestMatches.epcPpwMatch);

  return (
    <FormSection title="Pricing Options">
      {values.noPricingOptionsAvailable && (
        <Alert
          variant="warning"
          message={`No pricing options are available for this account.${
            accountData?.monthlyPaymentCap &&
            accountData.monthlyPaymentCap.orgMaxZeroEscalationRate !== undefined &&
            accountData.monthlyPaymentCap.zeroEscalationRate !==
              accountData.monthlyPaymentCap.orgMaxZeroEscalationRate
              ? ` This may be because all pricing options exceed this customer's maximum monthly payment cap.`
              : ``
          }`}
        ></Alert>
      )}
      {values.pricingOptionsLimitedByMonthlyPaymentCap && (
        <Alert
          variant="warning"
          message="Pricing options are currently limited based on the credit applicant or applicants (if there are co-applicants) on this account. Additional options may be available by adding a new co-applicant."
        ></Alert>
      )}
      <Box display="block">
        <Table
          rowKey="productId"
          columns={columns}
          isLoading={isLoading || isFetching}
          rows={escalationRateAndProductData?.tableData || []}
          isScrollable={{
            x: true,
          }}
        />
      </Box>
      {showLegend && (
        <Box display="flex" direction="row" gap="lg" padding="md 0 0 0">
          <Box display="flex" direction="row" gap="xs" alignItems="center">
            <Badge message={badgeConfig.kwhRate.label} variant={badgeConfig.kwhRate.variant} size="sm" />
            <span>Quoted Customer kWh Rate</span>
          </Box>
          <Box display="flex" direction="row" gap="xs" alignItems="center">
            <Badge
              message={badgeConfig.monthlyPayment.label}
              variant={badgeConfig.monthlyPayment.variant}
              size="sm"
            />
            <span>Closest Option to Quoted Monthly Payment</span>
          </Box>
          <Box display="flex" direction="row" gap="xs" alignItems="center">
            <Badge message={badgeConfig.epcPpw.label} variant={badgeConfig.epcPpw.variant} size="sm" />
            <span>Closest Option to Quoted EPC PPW</span>
          </Box>
        </Box>
      )}
    </FormSection>
  );
};
